/**
 * Shop Community Selector Web Component
 * -------------------------------------
 * Community selector button and modal for selecting user communities.
 *
 * NOTE: This component will eventually be replaced by a fragno implementation.
 *
 * Features:
 * - Community selector button (pill) with avatar display
 * - Modal with community grid selection
 * - Cookie and URL persistence
 * - Single/multiple community display
 *
 * Attributes:
 * - selected-communities: JSON array of selected community IDs
 * - show-banner: "true" | "false" - Show the green banner row (default: "true")
 *
 * Events dispatched:
 * - community-selector-open { bubbles, composed }
 * - community-selector-close { bubbles, composed }
 * - community-confirm { detail: { communities: string[] }, bubbles, composed }
 * - community-clear { bubbles, composed }
 *
 * Example:
 *   <shop-community-selector
 *     selected-communities='["military", "medical"]'
 *     show-banner="true">
 *   </shop-community-selector>
 */

import { dispatchTrack } from "../../../analytics/shop-analytics-track.js";

const ShopCommunitySelector = (function () {
  const TAG_NAME = "shop-community-selector";
  const SCROLL_LOCK_CLASS = "shop-community-modal-scroll-lock";

  function ensureScrollLockStyles() {
    const styleId = "shop-community-modal-scroll-lock-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `body.${SCROLL_LOCK_CLASS} { overflow: hidden !important; }`;
      document.head.appendChild(style);
    }
  }

  /**
   * Returns the icon URL for a community from its data object.
   * The URL is generated server-side (asset_path) and passed through
   * the available-communities JSON attribute as `icon_url`.
   */
  function getCommunityIconUrl(community) {
    return community.icon_url || null;
  }

  /** Hardcoded SVG for selected state (checkmark). Safe to use with innerHTML — no user data. */
  const CHECKED_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="11.67 3.5 5.25 10.5 2.33 7.58"></polyline>
  </svg>`;

  /** Hardcoded SVG for unselected state (plus icon). Safe to use with innerHTML — no user data. */
  const PLUS_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="7" y1="3.5" x2="7" y2="10.5"></line>
    <line x1="3.5" y1="7" x2="10.5" y2="7"></line>
  </svg>`;

  // Pre-parsed once from the static, non-interpolated markup above; toggling
  // clones the parsed content instead of re-assigning innerHTML per click.
  const CHECKED_SVG_TEMPLATE = document.createElement("template");
  CHECKED_SVG_TEMPLATE.innerHTML = CHECKED_SVG;
  const PLUS_SVG_TEMPLATE = document.createElement("template");
  PLUS_SVG_TEMPLATE.innerHTML = PLUS_SVG;

  /**
   * Generic communities icon (NOVA Design System, Foundation, node 61436-121),
   * rendered in the selector pill's zero state (no visible communities selected —
   * a hidden-only selection also resolves here via _visibleSelectedCommunities).
   * Pairs with the SHOPWEB-737 banner icon so the two read as one system. Fill-based;
   * the `.shop-community-selector__avatar svg` CSS rule drives the color via
   * `fill: currentColor` (white on the green-900 avatar background). Decorative: the
   * trigger button carries aria-label="Community selector", so the icon is
   * aria-hidden. Safe to use with innerHTML — no user data.
   */
  const COMMUNITIES_ICON_SVG = `<svg viewBox="0 0 29.0732 20" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.5371 13.4628C17.5922 13.463 20.3113 15.0637 22.2715 17.5585C22.5441 17.9058 22.4837 18.4086 22.1367 18.6816C21.7893 18.9546 21.2857 18.8942 21.0127 18.5468C19.285 16.348 16.9969 15.0636 14.5371 15.0634C12.0772 15.0634 9.78834 16.3478 8.06055 18.5468C7.7876 18.8941 7.28488 18.9544 6.9375 18.6816C6.59011 18.4087 6.52985 17.906 6.80273 17.5585C8.76301 15.0637 11.4819 13.4628 14.5371 13.4628ZM6.26074 12.4462C6.6347 12.4463 7.00418 12.4768 7.36621 12.5361C7.80169 12.6078 8.09746 13.0184 8.02637 13.4541C7.95497 13.8899 7.5433 14.1854 7.10742 14.1142C6.83015 14.0688 6.54742 14.0459 6.26074 14.0459C4.43917 14.0459 2.72962 14.9966 1.42871 16.6523C1.15576 16.9995 0.653015 17.0598 0.305664 16.7871C-0.0416093 16.5141 -0.101886 16.0114 0.170898 15.664C1.70427 13.7124 3.84389 12.4462 6.26074 12.4462ZM22.8125 12.4462C25.2293 12.4462 27.369 13.7125 28.9023 15.664C29.1751 16.0114 29.1149 16.5141 28.7676 16.7871C28.4202 17.0597 27.9175 16.9995 27.6445 16.6523C26.3436 14.9966 24.634 14.0459 22.8125 14.0459C22.5259 14.0459 22.243 14.0688 21.9658 14.1142C21.53 14.1853 21.1183 13.8899 21.0469 13.4541C20.9758 13.0185 21.2716 12.6079 21.707 12.5361C22.069 12.4768 22.4386 12.4463 22.8125 12.4462ZM14.5371 1.14742C17.333 1.14763 19.6003 3.41402 19.6006 6.20992C19.6006 9.0061 17.3332 11.2732 14.5371 11.2734C11.7408 11.2734 9.47363 9.00623 9.47363 6.20992C9.47396 3.41389 11.741 1.14742 14.5371 1.14742ZM6.26074 2.9814C6.77253 2.98147 7.26435 3.07634 7.71777 3.24996C8.13013 3.40809 8.33665 3.87067 8.17871 4.28316C8.02063 4.69547 7.55795 4.90187 7.14551 4.7441C6.87186 4.63932 6.57391 4.58108 6.26074 4.58101C4.89316 4.58101 3.78418 5.68999 3.78418 7.05757C3.78432 8.42503 4.89325 9.53413 6.26074 9.53413C6.56797 9.53407 6.86058 9.47792 7.12988 9.37691C7.54346 9.22186 8.00493 9.43117 8.16016 9.84468C8.31514 10.2581 8.10563 10.7196 7.69238 10.875C7.2461 11.0424 6.76281 11.1337 6.26074 11.1337C4.00959 11.1337 2.18471 9.30869 2.18457 7.05757C2.18457 4.80634 4.00951 2.9814 6.26074 2.9814ZM22.8125 2.9814C25.0637 2.9814 26.8887 4.80634 26.8887 7.05757C26.8885 9.30869 25.0636 11.1337 22.8125 11.1337C22.3105 11.1337 21.8271 11.0423 21.3809 10.875C20.9677 10.7195 20.7582 10.2581 20.9131 9.84468C21.0683 9.43123 21.5298 9.22197 21.9434 9.37691C22.2126 9.4779 22.5053 9.53405 22.8125 9.53413C24.18 9.53413 25.2889 8.42503 25.2891 7.05757C25.2891 5.68999 24.1801 4.58101 22.8125 4.58101C22.4994 4.5811 22.2013 4.63933 21.9277 4.7441C21.5153 4.90176 21.0526 4.69544 20.8945 4.28316C20.7366 3.87073 20.9432 3.40814 21.3555 3.24996C21.8088 3.07636 22.3008 2.98149 22.8125 2.9814ZM14.5371 2.74703C12.6247 2.74703 11.0745 4.29754 11.0742 6.20992C11.0742 8.12257 12.6245 9.67378 14.5371 9.67378C16.4496 9.67357 18 8.12244 18 6.20992C17.9997 4.29767 16.4494 2.74724 14.5371 2.74703Z"/>
  </svg>`;

  /**
   * Creates an avatar <div> element containing an optional community icon <img>.
   * Uses DOM APIs so user-influenced iconUrl values are never parsed as HTML.
   */
  function _createAvatarEl(iconUrl) {
    const wrapper = document.createElement("div");
    wrapper.className = "shop-community-selector__avatar";
    if (iconUrl) {
      const img = document.createElement("img");
      img.src = iconUrl;
      img.alt = "";
      img.setAttribute("aria-hidden", "true");
      wrapper.appendChild(img);
    }
    return wrapper;
  }

  function _createOverflowCountEl(count) {
    const wrapper = document.createElement("div");
    wrapper.className = "shop-community-selector__overflow-count";
    wrapper.textContent = `+${count}`;
    return wrapper;
  }

  /**
   * Zero-state avatar wrapper markup: the generic communities icon inside the
   * shared avatar wrapper. Emitted verbatim by both render() (initial paint) and
   * _updateSelectorDisplay() (selectedCount === 0); kept in one place so the two
   * sites cannot silently diverge. Safe to use with innerHTML — no user data.
   */
  function _zeroStateAvatarMarkup() {
    return `<div class="shop-community-selector__avatar">${COMMUNITIES_ICON_SVG}</div>`;
  }

  class ShopCommunitySelector extends HTMLElement {
    static get observedAttributes() {
      return [
        "selected-communities",
        "available-communities",
        "verified-communities",
        "modal-only",
      ];
    }

    constructor() {
      super();
      this._selectedCommunities = []; // Selected communities (committed state)
      this._tempSelectedCommunities = []; // Temporary state for modal (uncommitted)
      this._availableCommunities = []; // Available communities from Rails or default
      // Auth logic stays in Ruby (_header.haml); this is the pre-computed reset state.
      this._verifiedCommunities = []; // Reset target for Clear: user's verified groups (logged-in) or [] (logged-out)
    }

    connectedCallback() {
      if (!this._initialized) {
        // Load available communities from Rails
        const availableCommunitiesAttr = this.getAttribute(
          "available-communities",
        );
        if (availableCommunitiesAttr) {
          try {
            this._availableCommunities = JSON.parse(availableCommunitiesAttr);
          } catch (e) {
            console.error(
              "Failed to parse available-communities attribute:",
              e,
            );
            this._availableCommunities = [];
          }
        }

        // Load verified communities (pre-computed reset state from Rails).
        // Logged-in: user's verified group UIDs. Logged-out: [] (show all).
        const verifiedCommunitiesAttr = this.getAttribute(
          "verified-communities",
        );
        if (verifiedCommunitiesAttr) {
          try {
            const parsed = JSON.parse(verifiedCommunitiesAttr);
            this._verifiedCommunities = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            this._verifiedCommunities = [];
          }
        }

        // Load from attribute first (source of truth from Rails), then fall back to URL/cookie
        const selectedCommunitiesAttr = this.getAttribute(
          "selected-communities",
        );
        if (selectedCommunitiesAttr) {
          try {
            this._selectedCommunities = JSON.parse(selectedCommunitiesAttr);
          } catch (e) {
            console.error("Failed to parse selected-communities attribute:", e);
            this._selectedCommunities = [];
          }
        } else {
          this._loadCommunitySelectionFromURLOrCookie();
        }

        // Listen for custom event to open modal (from external triggers).
        // Store the bound reference so disconnectedCallback can remove it;
        // a removed selector must not keep answering this event from document.
        // `detail.anchor` (optional): the element to anchor the compact
        // dropdown to when this component renders no pill of its own
        // (modal-only). Supplied by the nav wiring, which owns the fragno
        // filters-cta pill.
        this._onOpenCommunitySelectorEvent = (event) =>
          this._handleSelectorClick(event?.detail?.anchor);
        document.addEventListener(
          "open-community-selector",
          this._onOpenCommunitySelectorEvent,
        );

        this.render();
        this._initialized = true;
        this._attachEventListeners();
        // Update the selector display to show selected community icons
        this._updateSelectorDisplay();
      }
    }

    disconnectedCallback() {
      this._detachEventListeners();
      if (this._onOpenCommunitySelectorEvent) {
        document.removeEventListener(
          "open-community-selector",
          this._onOpenCommunitySelectorEvent,
        );
      }
      document.body.classList.remove(SCROLL_LOCK_CLASS);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this._initialized) return;

      if (name === "selected-communities" && newValue) {
        try {
          this._selectedCommunities = JSON.parse(newValue);
          this._updateSelectorDisplay();
        } catch (e) {
          console.error("Failed to parse selected-communities attribute:", e);
        }
      }

      if (name === "available-communities" && newValue) {
        try {
          this._availableCommunities = JSON.parse(newValue);
        } catch (e) {
          console.error("Failed to parse available-communities attribute:", e);
          this._availableCommunities = [];
        }
      }

      // verified-communities is server-rendered once; dynamic updates are supported
      // for post-connection attribute changes (e.g. test harness), but removal is
      // intentionally a no-op — the attribute is never removed imperatively in practice.
      if (name === "verified-communities" && newValue) {
        try {
          const parsed = JSON.parse(newValue);
          this._verifiedCommunities = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error("Failed to parse verified-communities attribute:", e);
          this._verifiedCommunities = [];
        }
      }
    }

    _attachEventListeners() {
      // Community selector button
      const selector = this.querySelector(".shop-community-selector__selector");
      if (selector) {
        selector.removeEventListener("click", this._onSelectorClick);
        selector.addEventListener(
          "click",
          (this._onSelectorClick = this._handleSelectorClick.bind(this)),
        );
      }

      // Modal close button
      const closeBtn = this.querySelector(
        ".shop-community-selector__close-btn",
      );
      if (closeBtn) {
        closeBtn.removeEventListener("click", this._onCloseClick);
        closeBtn.addEventListener(
          "click",
          (this._onCloseClick = this._handleModalClose.bind(this)),
        );
      }

      // Modal backdrop
      const backdrop = this.querySelector(".shop-community-selector__backdrop");
      if (backdrop) {
        backdrop.removeEventListener("click", this._onBackdropClick);
        backdrop.addEventListener(
          "click",
          (this._onBackdropClick = this._handleModalClose.bind(this)),
        );
      }

      // Confirm button
      const confirmBtn = this.querySelector(
        ".shop-community-selector__confirm-btn",
      );
      if (confirmBtn) {
        confirmBtn.removeEventListener("click", this._onConfirmClick);
        confirmBtn.addEventListener(
          "click",
          (this._onConfirmClick = this._handleConfirm.bind(this)),
        );
      }

      // Clear button
      const clearBtn = this.querySelector(
        ".shop-community-selector__clear-btn",
      );
      if (clearBtn) {
        clearBtn.removeEventListener("click", this._onClearClick);
        clearBtn.addEventListener(
          "click",
          (this._onClearClick = this._handleClear.bind(this)),
        );
      }

      // Focus trap: capture-phase to intercept Tab before native browser focus movement
      this._boundHandleKeyDown = this._handleKeyDown.bind(this);
      document.addEventListener("keydown", this._boundHandleKeyDown, true);
    }

    _detachEventListeners() {
      const selector = this.querySelector(".shop-community-selector__selector");
      if (selector && this._onSelectorClick) {
        selector.removeEventListener("click", this._onSelectorClick);
      }

      const closeBtn = this.querySelector(
        ".shop-community-selector__close-btn",
      );
      if (closeBtn && this._onCloseClick) {
        closeBtn.removeEventListener("click", this._onCloseClick);
      }

      const backdrop = this.querySelector(".shop-community-selector__backdrop");
      if (backdrop && this._onBackdropClick) {
        backdrop.removeEventListener("click", this._onBackdropClick);
      }

      const confirmBtn = this.querySelector(
        ".shop-community-selector__confirm-btn",
      );
      if (confirmBtn && this._onConfirmClick) {
        confirmBtn.removeEventListener("click", this._onConfirmClick);
      }

      const clearBtn = this.querySelector(
        ".shop-community-selector__clear-btn",
      );
      if (clearBtn && this._onClearClick) {
        clearBtn.removeEventListener("click", this._onClearClick);
      }

      if (this._boundHandleKeyDown) {
        document.removeEventListener("keydown", this._boundHandleKeyDown, true);
      }

      // Tear down any open compact dropdown and its document-level listeners.
      this._closeCompactDropdown();
    }

    _handleKeyDown(event) {
      const modal = this.querySelector(".shop-community-selector__modal");
      if (!modal || modal.hasAttribute("hidden")) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this._handleModalClose();
        return;
      }

      if (event.key === "Tab") {
        event.preventDefault();
        this._manageFocusTrap(event.shiftKey);
      }
    }

    _manageFocusTrap(reverse) {
      const focusableElements = this._getFocusableElements();
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (reverse) {
        (currentIndex <= 0
          ? last
          : focusableElements[currentIndex - 1]
        ).focus();
      } else {
        (currentIndex === -1 || currentIndex >= focusableElements.length - 1
          ? first
          : focusableElements[currentIndex + 1]
        ).focus();
      }
    }

    _getFocusableElements() {
      const modal = this.querySelector(".shop-community-selector__modal");
      if (!modal) return [];

      const selector =
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return [...modal.querySelectorAll(selector)].filter((el) => {
        const style = window.getComputedStyle(el);
        return style.display !== "none" && style.visibility !== "hidden";
      });
    }

    _setInitialFocus() {
      const closeBtn = this.querySelector(
        ".shop-community-selector__close-btn",
      );
      if (closeBtn) closeBtn.focus();
    }

    _handleSelectorClick(externalAnchor) {
      // Seed the uncommitted working set for both interaction branches.
      this._tempSelectedCommunities = [...this._selectedCommunities];

      // In modal-only mode this component renders no pill of its own; the
      // trigger is the global nav's filters-cta, passed in as externalAnchor so
      // the compact dropdown can position relative to it.
      const anchor =
        externalAnchor instanceof Element
          ? externalAnchor
          : this.querySelector(".shop-community-selector__anchor");

      // Returning desktop users (a visible committed community already exists)
      // get the compact badge-anchored dropdown; first-time users and every
      // mobile user get the full-screen overlay. The compact dropdown is a
      // desktop-only affordance, so mobile keeps the overlay in every state.
      if (
        this._hasPreviouslySelectedCommunities() &&
        this._isCompactViewport()
      ) {
        this._openCompactDropdown(anchor);
      } else {
        this._openFullScreenModal();
      }

      this.dispatchEvent(
        new CustomEvent("community-selector-open", {
          bubbles: true,
          composed: true,
        }),
      );

      dispatchTrack(this, "community_selector_open", {});
    }

    _openFullScreenModal() {
      const modal = this.querySelector(".shop-community-selector__modal");
      if (!modal) {
        console.error("Modal element not found in component");
        return;
      }
      this._triggerElement = document.activeElement;
      modal.removeAttribute("hidden");
      this._populateCommunityGrid();
      ensureScrollLockStyles();
      document.body.classList.add(SCROLL_LOCK_CLASS);
      requestAnimationFrame(() => this._setInitialFocus());
    }

    // The committed communities currently visible in the pill: present in
    // _availableCommunities and not hidden (excludes the always-on
    // "all-members" background state and stale/unknown IDs). Single source of
    // truth shared by the pill renderer (_updateSelectorDisplay) and the
    // first-interaction gate, so the two can never disagree about what counts
    // as a visible selection.
    _visibleSelectedCommunities() {
      const list = Array.isArray(this._selectedCommunities)
        ? this._selectedCommunities
        : [];
      return list.filter((id) => {
        const community = this._availableCommunities.find((c) => c.id === id);
        return Boolean(community) && !community.hidden;
      });
    }

    // True iff a committed visible community exists — the first-interaction
    // signal. The only way to hold one is interact-then-confirm; existing
    // persistence (attribute / URL / cookie) replays it across navigation.
    _hasPreviouslySelectedCommunities() {
      return this._visibleSelectedCommunities().length > 0;
    }

    // The compact dropdown is desktop-only; mobile (<=1023px) keeps the
    // full-screen overlay in every state. Mirrors the @media breakpoint that
    // hides the dropdown CSS. Defaults to desktop when matchMedia is absent.
    _isCompactViewport() {
      return (
        typeof window.matchMedia !== "function" ||
        window.matchMedia("(min-width: 1024px)").matches
      );
    }

    _handleModalClose() {
      const modal = this.querySelector(".shop-community-selector__modal");
      if (modal) {
        modal.setAttribute("hidden", "");
        this._tempSelectedCommunities = [];
      }

      document.body.classList.remove(SCROLL_LOCK_CLASS);
      requestAnimationFrame(() => {
        this._triggerElement?.focus?.();
        this._triggerElement = null;
      });

      this.dispatchEvent(
        new CustomEvent("community-selector-close", {
          bubbles: true,
          composed: true,
        }),
      );
    }

    _handleConfirm() {
      // Tear down the compact dropdown (and its document listeners) before
      // committing. Apply navigates via window.location.href, but when the
      // resulting URL is unchanged (e.g. re-confirming the same selection)
      // that assignment is a no-op and never unloads the page — without this
      // the popover would stay open with live document listeners. No-op for
      // the full-screen modal path (no dropdown to close).
      this._closeCompactDropdown();

      // Commit temporary selections to actual state (deduplicated)
      this._selectedCommunities = [...new Set(this._tempSelectedCommunities)];
      this._tempSelectedCommunities = [];

      const selectedCommunities = this._selectedCommunities || [];

      // Save to cookie for persistence
      this._saveCommunitySelectionToCookie();

      // Dispatch event with selected communities
      this.dispatchEvent(
        new CustomEvent("community-confirm", {
          bubbles: true,
          composed: true,
          detail: { communities: selectedCommunities },
        }),
      );

      dispatchTrack(this, "community_selector_apply_click", {
        group_slugs: selectedCommunities,
      });

      // Navigate to URL with selected communities as query params
      const targetUrl = this._buildURLWithCommunities();
      window.location.href = targetUrl;
    }

    _handleClear() {
      // Logged-in: reset to verified communities. Logged-out: select all.
      // Auth logic stays in Ruby — _verifiedCommunities is pre-computed in _header.haml.
      this._tempSelectedCommunities =
        this._verifiedCommunities.length > 0
          ? [...this._verifiedCommunities]
          : this._availableCommunities.map((c) => c.id);

      // Re-populate whichever selection surface is open to reflect the reset.
      this._populateCommunityGrid();
      this._populateCompactList();

      // Dispatch event
      this.dispatchEvent(
        new CustomEvent("community-clear", {
          bubbles: true,
          composed: true,
        }),
      );

      dispatchTrack(this, "community_selector_reset_click", {});
    }

    // Builds a single community toggle <button> using DOM APIs only, so
    // user-influenced data (community label / icon_url) is never parsed as
    // HTML. Shared by the full-screen grid and the compact dropdown so the
    // XSS-safe construction lives in exactly one place.
    _createCommunityButton(community) {
      const isSelected = this._tempSelectedCommunities.includes(community.id);
      const iconUrl = getCommunityIconUrl(community);

      const btn = document.createElement("button");
      btn.className = `shop-community-selector__button${isSelected ? " shop-community-selector__button--selected" : ""}`;
      btn.dataset.communityId = community.id;
      btn.type = "button";

      const media = document.createElement("div");
      media.className = "shop-community-selector__button-media";
      if (iconUrl) {
        const img = document.createElement("img");
        img.src = iconUrl;
        img.alt = "";
        img.setAttribute("aria-hidden", "true");
        media.appendChild(img);
      }
      btn.appendChild(media);

      const labelEl = document.createElement("span");
      labelEl.className = "shop-community-selector__button-label";
      labelEl.textContent = community.label;
      btn.appendChild(labelEl);

      const toggleBtn = document.createElement("span");
      toggleBtn.className = "shop-community-selector__toggle-btn";
      toggleBtn.appendChild(
        (isSelected
          ? CHECKED_SVG_TEMPLATE
          : PLUS_SVG_TEMPLATE
        ).content.cloneNode(true),
      );
      btn.appendChild(toggleBtn);

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this._toggleCommunitySelection(btn);
      });
      return btn;
    }

    _toggleCommunitySelection(btn) {
      const communityId = btn.getAttribute("data-community-id");
      const isCurrentlySelected =
        this._tempSelectedCommunities.includes(communityId);

      // Update the temporary selection state (not committed until Confirm)
      if (isCurrentlySelected) {
        this._tempSelectedCommunities = this._tempSelectedCommunities.filter(
          (id) => id !== communityId,
        );
      } else {
        this._tempSelectedCommunities.push(communityId);
      }

      // Toggle the button's selected state WITHOUT rebuilding the container
      const toggleBtn = btn.querySelector(
        ".shop-community-selector__toggle-btn",
      );
      if (isCurrentlySelected) {
        btn.classList.remove("shop-community-selector__button--selected");
        toggleBtn.replaceChildren(PLUS_SVG_TEMPLATE.content.cloneNode(true));
      } else {
        btn.classList.add("shop-community-selector__button--selected");
        toggleBtn.replaceChildren(CHECKED_SVG_TEMPLATE.content.cloneNode(true));
      }
    }

    _populateCommunityGrid() {
      const grid = this.querySelector(".shop-community-selector__grid");
      if (!grid) return;

      this._tempSelectedCommunities = this._tempSelectedCommunities || [];
      grid.innerHTML = "";
      this._availableCommunities
        .filter((c) => !c.hidden)
        .forEach((community) => {
          grid.appendChild(this._createCommunityButton(community));
        });
    }

    // Returning-user path: a lightweight, badge-anchored popover that reuses
    // the temp-selection machinery, Apply (_handleConfirm) and Reset
    // (_handleClear). No focus trap and no body scroll-lock — those stay bound
    // to the full-screen modal only.
    _openCompactDropdown(anchorArg) {
      const anchor =
        anchorArg instanceof Element
          ? anchorArg
          : this.querySelector(".shop-community-selector__anchor");
      if (!anchor) {
        // The anchor ships in this component's own template (or is supplied by
        // the caller in modal-only mode), so a miss means a render regression —
        // surface it (don't silently degrade) before falling back to overlay.
        console.error("Community selector anchor not found");
        this._openFullScreenModal();
        return;
      }

      // When the anchor lives outside this component (modal-only mode, e.g. the
      // fragno filters-cta pill), the dropdown can't be a positioned child of
      // it. Append to <body> and position: fixed relative to the anchor's rect.
      const external = !this.contains(anchor);

      this._closeCompactDropdown(); // never stack two dropdowns
      this._triggerElement = document.activeElement;
      this._tempSelectedCommunities = this._tempSelectedCommunities || [];

      const dropdown = document.createElement("div");
      dropdown.className = "shop-community-selector__compact-dropdown";
      dropdown.setAttribute("role", "dialog");
      dropdown.setAttribute("aria-label", "Select your communities");

      const list = document.createElement("div");
      list.className = "shop-community-selector__compact-list";
      dropdown.appendChild(list);

      const actions = document.createElement("div");
      actions.className = "shop-community-selector__actions";

      const clearBtn = document.createElement("button");
      clearBtn.type = "button";
      clearBtn.className = "shop-community-selector__clear-btn";
      clearBtn.textContent = "Reset";
      clearBtn.addEventListener("click", () => this._handleClear());

      const confirmBtn = document.createElement("button");
      confirmBtn.type = "button";
      confirmBtn.className = "shop-community-selector__confirm-btn";
      confirmBtn.textContent = "Apply";
      confirmBtn.addEventListener("click", () => this._handleConfirm());

      actions.appendChild(clearBtn);
      actions.appendChild(confirmBtn);
      dropdown.appendChild(actions);

      if (external) {
        document.body.appendChild(dropdown);
        this._positionFixedDropdown(dropdown, anchor);
        // Keep the dropdown pinned to the anchor as the layout shifts.
        this._boundCompactReposition = () =>
          this._positionFixedDropdown(dropdown, anchor);
        window.addEventListener("resize", this._boundCompactReposition);
        window.addEventListener("scroll", this._boundCompactReposition, true);
      } else {
        anchor.appendChild(dropdown);
      }
      this._compactDropdown = dropdown;
      this._populateCompactList();

      // Close on Escape or an outside click. The outside-click binding is
      // deferred so the click that opened the dropdown does not immediately
      // close it. Bound refs are stored so _detachEventListeners can remove
      // them on disconnect (no document-listener leak).
      this._boundCompactKeyDown = (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          this._closeCompactDropdown();
          this._triggerElement?.focus?.();
        }
      };
      this._boundCompactOutsideClick = (event) => {
        if (!this._compactDropdown) return;
        // Clicks on the anchor (the trigger pill) or inside the dropdown itself
        // are "inside". When the dropdown is portaled to <body> (external
        // anchor), anchor.contains() no longer covers it, so check both.
        if (
          anchor.contains(event.target) ||
          this._compactDropdown.contains(event.target)
        ) {
          return;
        }
        this._closeCompactDropdown();
      };
      document.addEventListener("keydown", this._boundCompactKeyDown, true);
      setTimeout(() => {
        if (this._boundCompactOutsideClick) {
          document.addEventListener("click", this._boundCompactOutsideClick);
        }
      }, 0);
    }

    // Pin a body-portaled dropdown to the trigger's on-screen position.
    // Right-aligns the dropdown to the anchor's right edge (matching the
    // absolute-positioned variant's `right: 0`) and clamps within the viewport.
    _positionFixedDropdown(dropdown, anchor) {
      const rect = anchor.getBoundingClientRect();
      const gap = 8; // ~0.5rem, matches the absolute variant's top offset
      const margin = 16; // ~1rem viewport breathing room
      const width = dropdown.offsetWidth || 0;
      let left = rect.right - width;
      if (left < margin) left = margin;
      const maxLeft = window.innerWidth - width - margin;
      if (width && left > maxLeft) left = Math.max(margin, maxLeft);
      // Inline styles override the stylesheet's absolute-variant rules.
      dropdown.style.position = "fixed";
      dropdown.style.right = "auto";
      dropdown.style.top = `${rect.bottom + gap}px`;
      dropdown.style.left = `${left}px`;
    }

    _populateCompactList() {
      if (!this._compactDropdown) return;
      const list = this._compactDropdown.querySelector(
        ".shop-community-selector__compact-list",
      );
      if (!list) return;

      this._tempSelectedCommunities = this._tempSelectedCommunities || [];
      list.innerHTML = "";
      this._availableCommunities
        .filter((c) => !c.hidden)
        .forEach((community) => {
          list.appendChild(this._createCommunityButton(community));
        });
    }

    _closeCompactDropdown() {
      if (this._boundCompactKeyDown) {
        document.removeEventListener(
          "keydown",
          this._boundCompactKeyDown,
          true,
        );
        this._boundCompactKeyDown = null;
      }
      if (this._boundCompactOutsideClick) {
        document.removeEventListener("click", this._boundCompactOutsideClick);
        this._boundCompactOutsideClick = null;
      }
      if (this._boundCompactReposition) {
        window.removeEventListener("resize", this._boundCompactReposition);
        window.removeEventListener(
          "scroll",
          this._boundCompactReposition,
          true,
        );
        this._boundCompactReposition = null;
      }
      if (this._compactDropdown) {
        this._compactDropdown.remove();
        this._compactDropdown = null;
      }
    }

    _saveCommunitySelectionToCookie() {
      try {
        // Use the same format as Rails: UIDs joined with "&" (deduplicated)
        const uniqueCommunities = [...new Set(this._selectedCommunities || [])];
        const communitiesString = uniqueCommunities.join("&");
        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 2); // 2 year expiry to match Rails
        document.cookie = `selected_groups=${communitiesString}; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
      } catch (error) {
        console.error("Failed to save communities to cookie:", error);
      }
    }

    _buildURLWithCommunities() {
      try {
        const url = new URL(window.location);

        // Remove existing group_uids[] params
        url.searchParams.delete("group_uids[]");

        // Add current selected communities (deduplicated to prevent duplicate params)
        const uniqueCommunities = [...new Set(this._selectedCommunities)];
        uniqueCommunities.forEach((uid) => {
          url.searchParams.append("group_uids[]", uid);
        });

        return url.toString();
      } catch (error) {
        console.error("Failed to build URL with communities:", error);
        return window.location.href;
      }
    }

    _loadCommunitySelectionFromURLOrCookie() {
      try {
        // First, check URL params (takes priority over cookie)
        const urlParams = new URLSearchParams(window.location.search);
        const urlGroups = urlParams.getAll("group_uids[]");

        if (urlGroups.length > 0) {
          // URL params present - use them and sync to cookie (deduplicated)
          this._selectedCommunities = [...new Set(urlGroups)];
          this._saveCommunitySelectionToCookie();
        } else {
          // No URL params - fall back to cookie
          const cookieMatch = document.cookie.match(
            /(?:^|;\s*)selected_groups=([^;]*)/,
          );
          if (cookieMatch) {
            const communitiesString = decodeURIComponent(cookieMatch[1] || "");
            // Split by "&" to match Rails format, then filter out invalid IDs
            this._selectedCommunities = communitiesString
              ? communitiesString.split("&").filter((uid) => uid.length > 0)
              : [];
          } else {
            // No cookie found, ensure empty state
            this._selectedCommunities = [];
          }
        }

        // Always update display after loading/initializing
        this._updateSelectorDisplay();
      } catch (error) {
        console.error("Failed to load communities from URL or cookie:", error);
      }
    }

    _updateSelectorDisplay() {
      const selector = this.querySelector(".shop-community-selector__selector");
      if (!selector) return;

      // Ensure _selectedCommunities is an array
      this._selectedCommunities = Array.isArray(this._selectedCommunities)
        ? this._selectedCommunities
        : [];

      // Hidden ("all-members" background state) and stale/unknown IDs are
      // excluded from the pill and the +N overflow count (see
      // _visibleSelectedCommunities — the same source of truth the
      // first-interaction gate reads).
      const visibleSelected = this._visibleSelectedCommunities();
      const selectedCount = visibleSelected.length;

      if (selectedCount === 0) {
        // Zero state: icon-only generic communities icon (NOVA 61436-121),
        // rendered inside the avatar wrapper so the pill shape/size and
        // responsive layout are shared with the selected states. The trigger
        // button's aria-label names the control; the icon is decorative.
        selector.classList.remove("shop-community-selector__selector--group");
        selector.innerHTML = _zeroStateAvatarMarkup();
      } else if (selectedCount === 1) {
        // Single community: show single avatar with icon
        selector.classList.remove("shop-community-selector__selector--group");
        const communityId = visibleSelected[0];
        const community = this._availableCommunities.find(
          (c) => c.id === communityId,
        );
        const iconUrl = community ? getCommunityIconUrl(community) : null;
        selector.innerHTML = "";
        selector.appendChild(_createAvatarEl(iconUrl));
      } else {
        // Multiple communities: show two overlapping avatars. When 3+ are
        // selected, append a "+N" badge where N = selectedCount - 2.
        selector.classList.add("shop-community-selector__selector--group");
        selector.innerHTML = "";
        visibleSelected.slice(0, 2).forEach((communityId) => {
          const community = this._availableCommunities.find(
            (c) => c.id === communityId,
          );
          const iconUrl = community ? getCommunityIconUrl(community) : null;
          selector.appendChild(_createAvatarEl(iconUrl));
        });
        if (selectedCount > 2) {
          selector.appendChild(_createOverflowCountEl(selectedCount - 2));
        }
      }

      // Re-attach click handler after updating innerHTML
      selector.removeEventListener("click", this._onSelectorClick);
      selector.addEventListener(
        "click",
        (this._onSelectorClick = this._handleSelectorClick.bind(this)),
      );
    }

    render() {
      // Modal-only mode: the visible pill lives in the global nav
      // (fragno-global-nav's filters-cta). Suppress this component's own pill so
      // only one pill shows. The dropdown/modal is opened via the
      // `open-community-selector` event, anchored to the fragno pill (see
      // _handleSelectorClick / _openCompactDropdown external-anchor path).
      const modalOnly = this.hasAttribute("modal-only");
      const triggerMarkup = modalOnly
        ? ""
        : `
        <!-- Community Selector Button + compact-dropdown positioning anchor -->
        <div class="shop-community-selector__anchor">
          <button class="shop-community-selector__selector" type="button" aria-label="Community selector" aria-haspopup="true">
            <div class="shop-community-selector__avatar">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="36" height="36" rx="18" fill="#e5e5e5"/>
              </svg>
            </div>
          </button>
        </div>`;

      this.innerHTML = `
        <style>
          shop-community-selector {
            display: contents;
          }

          /* Community Selector Pill */
          .shop-community-selector__selector {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.25rem;
            min-width: 3rem;
            height: 3rem;
            padding: 0.125rem;
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border: 1px solid #d6d6d6;
            border-radius: 9999px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .shop-community-selector__selector:hover {
            background: rgba(255, 255, 255, 0.9);
          }

          .shop-community-selector__selector:focus {
            outline: 2px solid var(--colors-primary-default, #0066cc);
            outline-offset: 2px;
          }

          /* Button group style - symmetric padding so multi-avatar / +N states
             hug their content instead of trailing dead space on the right. */
          .shop-community-selector__selector--group {
            padding: 0.125rem;
          }

          .shop-community-selector__avatar {
            width: 2.25rem;
            height: 2.25rem;
            border-radius: 9999px;
            border: 2px solid var(--colors-white, #ffffff);
            box-shadow: 0px 1px 2px 0px rgba(10, 13, 18, 0.05);
            background: var(--colors-green-900, #084c2e);
            overflow: hidden;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .shop-community-selector__avatar + .shop-community-selector__avatar {
            margin-left: -0.75rem;
          }

          .shop-community-selector__avatar img {
            width: 1.25rem;
            height: 1.25rem;
            object-fit: contain;
            filter: brightness(0) invert(1);
          }

          /* Zero-state generic communities icon (inline SVG). The img filter
             above does not apply to inline <svg>, so drive color here.
             fill inherits currentColor, which resolves to white against the
             green-900 avatar background. Shares the selected-state img's 1.25rem
             layout box; the 29:20 viewBox is letterboxed within it
             (preserveAspectRatio default), so the drawn glyph is ~1.25rem wide. */
          .shop-community-selector__avatar svg {
            width: 1.25rem;
            height: 1.25rem;
            color: var(--colors-white, #ffffff);
            fill: currentColor;
          }

          .shop-community-selector__overflow-count {
            min-width: 2.25rem;
            height: 2.25rem;
            padding: 0 var(--spacing-sm);
            border-radius: var(--radius-full);
            border: 2px solid var(--colors-white, #ffffff);
            box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
            background: var(--colors-gray-100);
            color: var(--colors-gray-900);
            font-family: var(--font-family-body);
            font-size: var(--font-size-text-sm);
            font-weight: var(--font-weight-medium);
            line-height: var(--line-height-text-sm);
            letter-spacing: -0.03125rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            box-sizing: border-box;
          }

          .shop-community-selector__avatar + .shop-community-selector__overflow-count {
            margin-left: calc(-1 * var(--spacing-lg));
          }

          /* Compact dropdown (desktop, returning users) */
          .shop-community-selector__anchor {
            position: relative;
            display: inline-flex;
          }

          .shop-community-selector__compact-dropdown {
            position: absolute;
            top: calc(100% + 0.5rem);
            right: 0;
            z-index: 1001;
            width: 22rem;
            max-width: calc(100vw - 2rem);
            max-height: min(70vh, 32rem);
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding: 1rem 0;
            background: var(--colors-gray-100, #f5f5f5);
            border: 1px solid #d6d6d6;
            border-radius: 1.5rem;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .shop-community-selector__compact-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding: 0 1rem;
            overflow-y: auto;
          }

          /* Community Modal */
          .shop-community-selector__modal {
            position: fixed;
            inset: 0;
            z-index: 1001;
            display: none;
            align-items: center;
            justify-content: center;
            padding: 2rem;
          }

          .shop-community-selector__modal:not([hidden]) {
            display: flex;
          }

          .shop-community-selector__backdrop {
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(24px);
            -webkit-backdrop-filter: blur(24px);
          }

          .shop-community-selector__modal-content {
            position: relative;
            max-width: 35rem;
            width: 100%;
            background: var(--colors-gray-100, #f5f5f5);
            border-radius: 2rem;
            padding: 1.5rem 0;
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            max-height: calc(100dvh - 4rem);
            overflow: hidden;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .shop-community-selector__close-btn {
            position: absolute;
            top: 1.5rem;
            right: 1rem;
            width: 2.75rem;
            height: 2.75rem;
            display: flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: none;
            border-radius: 0.5rem;
            cursor: pointer;
            color: #141414;
            transition: background 0.2s ease;
          }

          .shop-community-selector__close-btn:hover {
            background: rgba(0, 0, 0, 0.05);
          }

          .shop-community-selector__modal-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            padding: 0 1rem;
            text-align: center;
          }

          .shop-community-selector__modal-icon {
            width: 3rem;
            height: 3rem;
            border-radius: 1rem;
            background: radial-gradient(circle at center, #3d7150 0%, #254832 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
          }

          .shop-community-selector__modal-icon::after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 1rem;
            background: radial-gradient(circle at bottom, rgba(175, 221, 192, 0.2) 0%, transparent 100%);
            mix-blend-mode: plus-lighter;
          }

          .shop-community-selector__modal-title {
            font-family: var(--font-family-body, 'Inter', sans-serif);
            font-size: 1.5rem;
            font-weight: 500;
            line-height: 2rem;
            letter-spacing: -0.03rem;
            color: #000;
            margin: 0;
          }

          .shop-community-selector__modal-subtitle {
            font-family: var(--font-family-body, 'Inter', sans-serif);
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5rem;
            letter-spacing: -0.005rem;
            color: #424242;
            margin: 0;
          }

          .shop-community-selector__grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
            padding: 0 1rem;
            flex: 1;
            overflow-y: auto;
          }

          .shop-community-selector__button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem;
            border: 2px solid var(--colors-gray-400, #a3a3a3);
            border-radius: 1.5rem;
            background: var(--colors-gray-100, #f5f5f5);
            cursor: pointer;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .shop-community-selector__button--selected {
            border-color: var(--colors-gray-900, #141414);
            background: var(--colors-gray-100, #f5f5f5);
          }

          .shop-community-selector__button:hover {
            background: #ececec;
            border-color: #d6d6d6;
          }

          .shop-community-selector__button--selected:hover {
            background: #ececec;
            border-color: var(--colors-gray-900, #141414);
          }

          .shop-community-selector__button-media {
            width: 2.5rem;
            height: 2.5rem;
            border-radius: var(--radius-2xl, 1rem);
            background: var(--colors-white, #ffffff);
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--spacing-md, 0.5rem);
            box-sizing: border-box;
          }

          .shop-community-selector__button-media img {
            width: 1.5rem;
            height: 1.5rem;
            object-fit: contain;
          }

          .shop-community-selector__button--selected .shop-community-selector__button-media {
            background: var(--colors-green-900, #084c2e);
          }

          .shop-community-selector__button--selected .shop-community-selector__button-media img {
            filter: brightness(0) invert(1);
          }

          .shop-community-selector__button-label {
            flex: 1;
            padding: 0 0.75rem;
            font-family: var(--font-family-body, 'Inter', sans-serif);
            font-size: 1rem;
            line-height: 1.5rem;
            letter-spacing: -0.005rem;
            color: #141414;
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .shop-community-selector__button--selected .shop-community-selector__button-label {
            font-weight: 500;
          }

          .shop-community-selector__toggle-btn {
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border: 1.5px solid #d6d6d6;
            background: var(--colors-gray-100, #f5f5f5);
            color: var(--colors-gray-900, #141414);
            cursor: pointer;
          }

          .shop-community-selector__button--selected .shop-community-selector__toggle-btn {
            background: #141414;
            border-color: #141414;
          }

          .shop-community-selector__toggle-btn svg {
            width: 0.8333rem;
            height: 0.8333rem;
          }

          .shop-community-selector__actions {
            display: flex;
            gap: 0.75rem;
            padding: 0 1rem;
          }

          .shop-community-selector__clear-btn,
          .shop-community-selector__confirm-btn {
            flex: 1;
            height: 3.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.75rem 1.125rem;
            border-radius: 9999px;
            font-family: var(--font-family-body, 'Inter', sans-serif);
            font-size: 1rem;
            font-weight: 500;
            line-height: 1.5rem;
            letter-spacing: -0.005rem;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .shop-community-selector__clear-btn {
            background: transparent;
            border: none;
            color: #141414;
            text-decoration: underline;
          }

          .shop-community-selector__clear-btn:hover {
            background: rgba(0, 0, 0, 0.05);
          }

          .shop-community-selector__confirm-btn {
            background: #141414;
            border: none;
            color: #FFFFFF;
          }

          .shop-community-selector__confirm-btn:hover {
            background: #000000;
          }

          @media (max-width: 1023px) {
            .shop-community-selector__banner {
              display: none;
            }

            /* Mobile keeps the full-screen overlay in every state. */
            .shop-community-selector__compact-dropdown {
              display: none;
            }

            .shop-community-selector__modal {
              padding: 0.5rem;
              height: 100dvh;
            }

            .shop-community-selector__modal-content {
              max-width: 100%;
              border-radius: 2rem;
              padding: 1rem 0;
              gap: 0.75rem;
              max-height: calc(100dvh - 1rem);
            }

            .shop-community-selector__modal-header {
              gap: 0.5rem;
            }

            .shop-community-selector__modal-title {
              font-size: 1.25rem;
              line-height: 1.75rem;
            }

            .shop-community-selector__grid {
              grid-template-columns: 1fr;
              gap: 0.5rem;
            }

            .shop-community-selector__actions {
              flex-direction: column;
              gap: 0.5rem;
            }

            .shop-community-selector__clear-btn,
            .shop-community-selector__confirm-btn {
              flex: none;
              width: 100%;
              height: 2.75rem;
            }

            .shop-community-selector__clear-btn {
              order: 2;
            }

            .shop-community-selector__confirm-btn {
              order: 1;
            }
          }
        </style>

        ${triggerMarkup}

        <!-- Community Modal -->
        <div class="shop-community-selector__modal" hidden role="dialog" aria-modal="true" aria-labelledby="shop-community-selector-title">
          <!-- Backdrop -->
          <div class="shop-community-selector__backdrop"></div>

          <!-- Modal Content -->
          <div class="shop-community-selector__modal-content">
            <!-- Close Button -->
            <button class="shop-community-selector__close-btn" type="button" aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <!-- Header -->
            <div class="shop-community-selector__modal-header">
              <div class="shop-community-selector__modal-icon">
                <svg width="29" height="20" viewBox="0 0 29 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="14.5" cy="5.5" r="3" stroke="white" stroke-width="1.5" fill="none"/>
                  <path d="M20.5 18.5c0-3.314-2.686-6-6-6h-1c-3.314 0-6 2.686-6 6" stroke="white" stroke-width="1.5" stroke-linecap="round" fill="none"/>
                  <circle cx="22.5" cy="6.5" r="2" stroke="white" stroke-width="1.5" fill="none"/>
                  <path d="M26.5 18.5c0-2.209-1.791-4-4-4h-.5" stroke="white" stroke-width="1.5" stroke-linecap="round" fill="none"/>
                  <circle cx="6.5" cy="6.5" r="2" stroke="white" stroke-width="1.5" fill="none"/>
                  <path d="M2.5 18.5c0-2.209 1.791-4 4-4h.5" stroke="white" stroke-width="1.5" stroke-linecap="round" fill="none"/>
                </svg>
              </div>
              <h2 class="shop-community-selector__modal-title" id="shop-community-selector-title">Get benefits that match you</h2>
              <p class="shop-community-selector__modal-subtitle">Select your communities to personalize your experience.</p>
            </div>

            <!-- Communities Grid -->
            <div class="shop-community-selector__grid">
              <!-- These will be populated dynamically -->
            </div>

            <!-- Actions -->
            <div class="shop-community-selector__actions">
              <button class="shop-community-selector__clear-btn" type="button">Reset</button>
              <button class="shop-community-selector__confirm-btn" type="button">Apply</button>
            </div>
          </div>
        </div>
      `;
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopCommunitySelector);
  }

  return ShopCommunitySelector;
})();

export { ShopCommunitySelector };
